import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { DangerButton, Modal, PrimaryButton, SecondaryButton } from '../../components';
import { manageDisableScrolling } from '../../ducks/UI.duck';

import css from './ConfirmationModal.module.css';

/**
 * The Components board's confirm ("Leave Signal Works?" · Stay / Leave room): an optional icon
 * tile, a title, a line of text, and Cancel plus Confirm on the right. `danger` makes the confirm
 * a DangerButton and the tile red, for leaving, deleting and cancelling for good. The family
 * (public or dashboard) comes from the page, so the same modal serves both.
 */
const ConfirmationModal = props => {
  const {
    className,
    rootClassName,
    intl,
    id = 'ConfirmationModal',
    isOpen,
    onCloseModal,
    onConfirm,
    onCancel,
    inProgress,
    title,
    description,
    confirmMessage,
    cancelMessage,
    icon,
    danger,
    onManageDisableScrolling,
  } = props;

  const modalId = id || 'ConfirmationModal';
  const titleId = `${modalId}.title`;
  const classes = classNames(rootClassName || css.root, className);
  const ConfirmButton = danger ? DangerButton : PrimaryButton;

  const titleMessage = title || intl.formatMessage({ id: 'ConfirmationModal.title' });
  const descriptionMessage =
    description || intl.formatMessage({ id: 'ConfirmationModal.description' });
  const confirmMessageText =
    confirmMessage || intl.formatMessage({ id: 'ConfirmationModal.confirm' });
  const cancelMessageText = cancelMessage || intl.formatMessage({ id: 'ConfirmationModal.cancel' });

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      id={modalId}
      containerClassName={classes}
      contentClassName={css.content}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      ariaLabelledBy={titleId}
      usePortal
    >
      {icon ? (
        <span className={classNames(css.icon, { [css.iconDanger]: danger })} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <h2 id={titleId} className={classNames(css.title, { [css.titleWithoutIcon]: !icon })}>
        {titleMessage}
      </h2>
      <p className={css.message}>{descriptionMessage}</p>
      <div className={css.buttons}>
        <SecondaryButton type="button" className={css.button} onClick={handleCancel}>
          {cancelMessageText}
        </SecondaryButton>
        <ConfirmButton
          type="button"
          className={css.button}
          inProgress={inProgress}
          onClick={handleConfirm}
        >
          {confirmMessageText}
        </ConfirmButton>
      </div>
    </Modal>
  );
};

const { bool, string, func, node } = PropTypes;

ConfirmationModal.defaultProps = {
  className: null,
  rootClassName: null,
  id: null,
  isOpen: false,
  inProgress: false,
  onConfirm: null,
  onCloseModal: null,
  title: null,
  description: null,
  confirmMessage: null,
  cancelMessage: null,
  icon: null,
  danger: false,
};

ConfirmationModal.propTypes = {
  className: string,
  rootClassName: string,
  id: string,
  intl: intlShape.isRequired,
  isOpen: bool.isRequired,
  inProgress: bool.isRequired,
  onConfirm: func,
  onCloseModal: func,
  title: string,
  description: string,
  confirmMessage: string,
  cancelMessage: string,
  // The tile above the title (a glyph or an icon); the board's confirm has one
  icon: node,
  // A DangerButton and a red tile, for leaving, deleting and cancelling for good
  danger: bool,
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

export default compose(injectIntl, connect(null, mapDispatchToProps))(ConfirmationModal);
